import { CITY_NAMES } from './lore.js';
import { Rng } from './rng.js';
import { POCKETS, REGION } from './pockets.generated.js';
import { createUnit } from './defence.js';
import { REGIONAL_WEAPONS } from './scope.js';
import { ALL_ASSET_TYPES, ASSET_SPECS, WEAPON_SPECS, CITY_H, CITY_W, CLUSTERS_PER_REGION, EXTENDED_RANGE_ADD_KM, REACH_RULE, START_ARSENAL, START_DEFENCES, START_FEAR, START_LOYALTY, START_POPULATION, URBAN_HP_PER_KPOP, START_RESOURCES, START_UNREST, } from './constants.js';
import { DEVELOPMENT_SPECS } from './constants.js';
// ---------------------------------------------------------------------------
// City generation.
//
// Every city gets the exact same asset roster, so no player starts richer. What
// is randomised is where everything sits, which is the only thing the enemy has
// to work out. Symmetric in substance, asymmetric in shape.
// ---------------------------------------------------------------------------
/**
 * How strongly *something* wants the middle (1) or the rim (-1).
 *
 * These are the shape of a city — a dense core, an industrial ring, an airfield
 * and farmland out at the edge — but which kind of thing takes which ring is
 * decided per city by `districtPlan`, not fixed here. A fixed table meant every
 * command bunker in the game sat downtown and every radar sat on the rim, so
 * after two matches nobody needed to scout anything: you aimed at the middle
 * and hit headquarters. Now the roster is known and the arrangement is not,
 * which is the game a player should actually be playing.
 */
const CENTRALITY = {
    command: 0.9,
    comms: 0.7,
    residential: 0.5,
    hospital: 0.4,
    power_plant: -0.2,
    factory: -0.3,
    water_works: -0.4,
    depot: -0.5,
    barracks: -0.5,
    silo: -0.7,
    radar: -0.8,
    refinery: -0.8,
    airbase: -0.9,
    farm: -1.0,
};
/** Minimum tile separation between two assets of the same type. Spreads eggs across baskets. */
const SELF_SPACING = {
    silo: 8,
    radar: 9,
    power_plant: 7,
    barracks: 6,
    depot: 6,
    hospital: 7,
    factory: 4,
    residential: 3,
};
/** A city's name: sixty-four a side, so a full war never repeats one (lore.ts). */
export function cityName(faction, index) {
    const pool = CITY_NAMES[faction];
    return pool[index % pool.length];
}
// --- terrain ---------------------------------------------------------------
function generateTerrain(rng) {
    const t = new Array(CITY_W * CITY_H).fill('ground');
    const at = (x, y) => y * CITY_W + x;
    // A river. Runs roughly north-south or east-west, meandering, 1-2 tiles wide.
    const vertical = rng.chance(0.5);
    let pos = rng.range(6, (vertical ? CITY_W : CITY_H) - 7);
    const span = vertical ? CITY_H : CITY_W;
    let prev = null;
    for (let i = 0; i < span; i++) {
        pos += rng.range(-1, 1);
        pos = Math.max(2, Math.min((vertical ? CITY_W : CITY_H) - 3, pos));
        const width = rng.chance(0.3) ? 2 : 1;
        // A sideways step on a one-tile river leaves this row's water meeting the
        // last row's only at a corner, and a river joined at corners draws as a
        // staircase of separately walled pools. Widening the row back to where the
        // last one started guarantees the two share an edge. No extra draws are
        // taken, so every other property of the seed is unchanged.
        const from = prev == null ? pos : Math.min(pos, prev);
        const to = prev == null ? pos + width - 1 : Math.max(pos + width - 1, prev);
        for (let p = from; p <= to; p++) {
            if (vertical)
                t[at(p, i)] = 'water';
            else
                t[at(i, p)] = 'water';
        }
        prev = pos;
    }
    // Two or three parks. Buildable, but they read as open ground.
    const parks = rng.range(2, 3);
    for (let p = 0; p < parks; p++) {
        const pw = rng.range(2, 4);
        const ph = rng.range(2, 4);
        const px = rng.range(1, CITY_W - pw - 1);
        const py = rng.range(1, CITY_H - ph - 1);
        for (let y = py; y < py + ph; y++) {
            for (let x = px; x < px + pw; x++) {
                if (t[at(x, y)] === 'ground')
                    t[at(x, y)] = 'park';
            }
        }
    }
    // A road spine crossing the city, plus one branch. Purely visual grounding.
    const roadY = rng.range(5, CITY_H - 6);
    const roadX = rng.range(5, CITY_W - 6);
    // Where a road meets the river it bridges it. It used to skip the water and
    // stop dead at each bank, leaving every road in two pieces.
    for (let x = 0; x < CITY_W; x++)
        t[at(x, roadY)] = t[at(x, roadY)] === 'water' ? 'bridge' : 'road';
    for (let y = 0; y < CITY_H; y++) {
        const i = at(roadX, y);
        t[i] = t[i] === 'water' || t[i] === 'bridge' ? 'bridge' : 'road';
    }
    return t;
}
// --- asset placement -------------------------------------------------------
function footprintFree(occupied, terrain, x, y, w, h) {
    if (x < 0 || y < 0 || x + w > CITY_W || y + h > CITY_H)
        return false;
    for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
            const i = (y + dy) * CITY_W + (x + dx);
            if (occupied[i])
                return false;
            // Targets stand on land: never in the river, on a road or on a bridge.
            if (terrain[i] === 'water' || terrain[i] === 'road' || terrain[i] === 'bridge')
                return false;
        }
    }
    return true;
}
function centerDistance(x, y, w, h) {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const dx = (cx - CITY_W / 2) / (CITY_W / 2);
    const dy = (cy - CITY_H / 2) / (CITY_H / 2);
    return Math.sqrt(dx * dx + dy * dy); // 0 at centre, ~1 at the corners
}
/**
 * This city's district plan.
 *
 * The preferences above are dealt back out across the asset types at random,
 * so the set of rings a city has is always the same — one thing badly wants the
 * core, one badly wants the rim — but which thing is anybody's guess until you
 * have looked. Each is then scaled a little, so two cities that happen to draw
 * the same deal still do not lay out identically.
 */
function districtPlan(rng) {
    const types = [...ALL_ASSET_TYPES];
    const wants = types.map((t) => CENTRALITY[t]);
    for (let i = wants.length - 1; i > 0; i--) {
        const j = rng.int(i + 1);
        const t = wants[i];
        wants[i] = wants[j];
        wants[j] = t;
    }
    const plan = {};
    types.forEach((type, i) => {
        plan[type] = wants[i] * (0.75 + rng.float(0, 0.5));
    });
    return plan;
}
function placeAssets(rng, terrain) {
    const plan = districtPlan(rng);
    const occupied = new Uint8Array(CITY_W * CITY_H);
    const assets = [];
    let nextId = 0;
    // Big footprints first — they are the hardest to fit, and a greedy fill would
    // otherwise leave them nowhere to go.
    const queue = [];
    for (const type of ALL_ASSET_TYPES) {
        for (let i = 0; i < ASSET_SPECS[type].count; i++)
            queue.push(type);
    }
    queue.sort((a, b) => {
        const sa = ASSET_SPECS[a];
        const sb = ASSET_SPECS[b];
        return sb.w * sb.h - sa.w * sa.h;
    });
    for (const type of queue) {
        const spec = ASSET_SPECS[type];
        const want = plan[type];
        const spacing = SELF_SPACING[type] ?? 0;
        const sameType = assets.filter((a) => a.type === type);
        // Sample candidates and keep the one that best matches the type's taste for
        // the centre or the rim, while respecting same-type spacing.
        let best = null;
        for (let attempt = 0; attempt < 220; attempt++) {
            const x = rng.int(CITY_W - spec.w + 1);
            const y = rng.int(CITY_H - spec.h + 1);
            if (!footprintFree(occupied, terrain, x, y, spec.w, spec.h))
                continue;
            let minSep = Infinity;
            for (const other of sameType) {
                const d = Math.hypot(other.x - x, other.y - y);
                if (d < minSep)
                    minSep = d;
            }
            const spacingOk = sameType.length === 0 || minSep >= spacing;
            // Late attempts relax spacing rather than fail to place at all.
            if (!spacingOk && attempt < 160)
                continue;
            const dist = centerDistance(x, y, spec.w, spec.h);
            // want > 0 prefers small dist; want < 0 prefers large dist.
            // The jitter is deliberately large against the preference: a district
            // plan should be a tendency, not a law. With it small, a type that drew
            // a rim preference went to a corner every single time and a scout could
            // skip the corners on the next city.
            let score = -want * dist + rng.float(0, 0.55);
            if (!spacingOk)
                score -= 0.5;
            if (best === null || score > best.score)
                best = { x, y, score };
        }
        if (!best)
            continue; // genuinely nowhere left; the roster stays symmetric by count of what fit
        for (let dy = 0; dy < spec.h; dy++) {
            for (let dx = 0; dx < spec.w; dx++) {
                occupied[(best.y + dy) * CITY_W + (best.x + dx)] = 1;
            }
        }
        assets.push({
            id: `a${nextId++}`,
            type,
            cls: spec.cls,
            x: best.x,
            y: best.y,
            w: spec.w,
            h: spec.h,
            hp: spec.hp,
            maxHp: spec.hp,
            pop: spec.pop,
            housed: spec.pop,
            offlineFor: 0,
        });
    }
    return assets;
}
const MAX_URBAN_BLOCKS = 80;
const MAX_MODEL_COPIES = 2;
const MAX_TREE_COPIES = 2;
export const FABRIC_MODELS = [
    { slug: '01-tenement-slab-narrow', w: 1, h: 2, height: 'tall' },
    { slug: '02-tenement-slab-broad', w: 2, h: 1, height: 'tall' },
    { slug: '03-walkup-corner', w: 2, h: 2, height: 'mid' },
    { slug: '04-walkup-linear', w: 1, h: 2, height: 'mid' },
    { slug: '05-point-tower-square', w: 1, h: 1, height: 'tall' },
    { slug: '06-point-tower-offset', w: 1, h: 1, height: 'tall' },
    { slug: '07-courtyard-u-block', w: 2, h: 2, height: 'mid' },
    { slug: '08-terrace-row', w: 1, h: 2, height: 'low' },
    { slug: '09-prefab-dormitory', w: 2, h: 1, height: 'mid' },
    { slug: '10-workers-hostel', w: 1, h: 2, height: 'mid' },
    { slug: '11-tower-podium', w: 2, h: 2, height: 'tall' },
    { slug: '12-balcony-block', w: 2, h: 1, height: 'tall' },
    { slug: '13-shuttered-shop-row', w: 1, h: 2, height: 'low' },
    { slug: '14-market-hall', w: 2, h: 2, height: 'low' },
    { slug: '15-office-annex', w: 1, h: 2, height: 'mid' },
    { slug: '16-office-block', w: 2, h: 2, height: 'tall' },
    { slug: '17-parking-garage', w: 2, h: 2, height: 'mid' },
    { slug: '18-budget-hotel', w: 1, h: 2, height: 'mid' },
    { slug: '19-department-store', w: 2, h: 2, height: 'mid' },
    { slug: '20-service-station', w: 2, h: 2, height: 'low' },
    { slug: '21-retail-podium', w: 2, h: 2, height: 'mid' },
    { slug: '22-corner-commercial', w: 2, h: 2, height: 'mid' },
    { slug: '23-primary-school', w: 2, h: 2, height: 'low' },
    { slug: '24-concrete-library', w: 2, h: 2, height: 'mid' },
    { slug: '25-fire-station', w: 2, h: 1, height: 'low' },
    { slug: '26-police-station', w: 2, h: 2, height: 'mid' },
    { slug: '27-community-centre', w: 2, h: 2, height: 'low' },
    { slug: '28-sports-hall', w: 2, h: 2, height: 'low' },
    { slug: '29-clinic', w: 1, h: 2, height: 'low' },
    { slug: '30-council-office', w: 2, h: 1, height: 'mid' },
    { slug: '31-sawtooth-workshop', w: 2, h: 2, height: 'low' },
    { slug: '32-long-warehouse', w: 1, h: 2, height: 'low' },
    { slug: '33-loading-depot', w: 2, h: 2, height: 'low' },
    { slug: '34-boiler-house', w: 1, h: 1, height: 'low' },
    { slug: '35-electrical-substation', w: 2, h: 1, height: 'low' },
    { slug: '36-telecom-exchange', w: 1, h: 2, height: 'mid' },
    { slug: '37-waste-transfer', w: 2, h: 2, height: 'low' },
    { slug: '38-machine-shop', w: 2, h: 1, height: 'low' },
    { slug: '39-cold-store', w: 1, h: 2, height: 'low' },
    { slug: '40-pump-house', w: 1, h: 1, height: 'low' },
    { slug: '41-maintenance-shed', w: 1, h: 2, height: 'low' },
    { slug: '42-small-foundry', w: 2, h: 2, height: 'low' },
    { slug: '43-bus-depot', w: 2, h: 2, height: 'low' },
    { slug: '44-tram-shed', w: 2, h: 2, height: 'low' },
    { slug: '45-service-yard-office', w: 1, h: 1, height: 'low' },
    { slug: '46-railway-warehouse', w: 1, h: 2, height: 'low' },
    { slug: '47-signal-building', w: 1, h: 1, height: 'low' },
    { slug: '48-unfinished-frame', w: 2, h: 2, height: 'mid' },
    { slug: '49-condemned-corner', w: 2, h: 2, height: 'mid' },
    { slug: '50-stripped-office', w: 2, h: 2, height: 'mid' },
];
/**
 * Every passive building the generator can place, as manifest keys. Exported
 * so tools that must mirror the game -- the model audit -- read the list the
 * game actually uses instead of keeping a copy that can drift from it.
 */
export const FABRIC_MODEL_KEYS = FABRIC_MODELS.map(m => `fabric:${m.slug}`);
/**
 * Hitpoints of every passive block in a city of `blocks` of them: the
 * starting population's share, flat between buildings (URBAN_HP_PER_KPOP).
 */
export function urbanBlockMaxHp(blocks) {
    return Math.max(1, Math.round(START_POPULATION * URBAN_HP_PER_KPOP / Math.max(1, blocks)));
}
const TREE_MODELS = [
    '51-leafless-plane-tree', '52-stressed-plane-tree', '53-pollarded-street-tree',
    '54-narrow-poplar', '55-scraggly-birch', '56-wind-bent-conifer',
    '57-dead-tree-snag', '58-neglected-rowan', '59-coppiced-multistem',
    '60-overgrown-park-tree',
];
/**
 * The land the passive fabric can go on around a set of strategic buildings,
 * and the bookkeeping for claiming it. Shared by generation and by City
 * Reorganisation, so both lay blocks out by exactly the same rule.
 */
function fabricLand(terrain, assets) {
    const occupied = new Set();
    for (const a of assets) {
        for (let dy = 0; dy < a.h; dy++)
            for (let dx = 0; dx < a.w; dx++) {
                occupied.add((a.y + dy) * CITY_W + a.x + dx);
            }
    }
    const usable = (x, y) => {
        if (x < 0 || y < 0 || x >= CITY_W || y >= CITY_H)
            return false;
        const i = y * CITY_W + x;
        return terrain[i] === 'ground' && !occupied.has(i);
    };
    const available = (x, y, w, h) => {
        for (let dy = 0; dy < h; dy++)
            for (let dx = 0; dx < w; dx++) {
                if (!usable(x + dx, y + dy))
                    return false;
            }
        return true;
    };
    const claim = (x, y, w, h) => {
        for (let dy = 0; dy < h; dy++)
            for (let dx = 0; dx < w; dx++) {
                occupied.add((y + dy) * CITY_W + x + dx);
            }
    };
    const cells = Array.from({ length: CITY_W * CITY_H }, (_, i) => ({ x: i % CITY_W, y: Math.floor(i / CITY_W) }));
    return { terrain, occupied, available, claim, cells };
}
/**
 * Where a block of this footprint goes: street frontage and neighbours
 * preferred, with seeded variation. Strategic assets are already reserved and
 * never displaced by scenery. Every candidate draws from the stream, in the
 * same order, whoever is asking.
 */
function fabricSite(rng, land, w, h) {
    const ranked = rng.shuffle([...land.cells]).filter(({ x, y }) => land.available(x, y, w, h))
        .map(site => {
        let frontage = 0, neighbours = 0;
        for (let y = site.y - 1; y <= site.y + h; y++)
            for (let x = site.x - 1; x <= site.x + w; x++) {
                if (x < 0 || y < 0 || x >= CITY_W || y >= CITY_H)
                    continue;
                const i = y * CITY_W + x;
                if (land.terrain[i] === 'road')
                    frontage++;
                if (land.occupied.has(i))
                    neighbours++;
            }
        return { ...site, score: frontage * .65 + Math.min(neighbours, 8) * .3 + rng.float(0, 2) };
    }).sort((a, b) => b.score - a.score);
    return ranked[0] ?? null;
}
/** Once building placement is complete, unclaimed buildable land becomes parkland. */
function fillParks(land) {
    for (let i = 0; i < land.terrain.length; i++) {
        if (land.terrain[i] === 'ground' && !land.occupied.has(i))
            land.terrain[i] = 'park';
    }
}
/** The open park tiles trees can stand on, in seeded order. */
function treeSites(rng, land) {
    return rng.shuffle(land.cells.filter(({ x, y }) => {
        const i = y * CITY_W + x;
        return land.terrain[i] === 'park' && !land.occupied.has(i);
    }));
}
/** Place the complete passive layout once, after strategic assets. */
function placeCityFabric(rng, terrain, assets) {
    const land = fabricLand(terrain, assets);
    const urban = [];
    const candidates = rng.shuffle(FABRIC_MODELS.flatMap((model) => Array.from({ length: MAX_MODEL_COPIES }, () => model)));
    for (const model of candidates) {
        if (urban.length >= MAX_URBAN_BLOCKS)
            break;
        const site = fabricSite(rng, land, model.w, model.h);
        if (!site)
            continue;
        land.claim(site.x, site.y, model.w, model.h);
        const inset = .045 + rng.float(0, .035);
        const w = model.w - inset * 2;
        const h = model.h - inset * 2;
        const z = model.height === 'tall' ? rng.float(1.18, 1.5)
            : model.height === 'mid' ? rng.float(.84, 1.16) : rng.float(.5, .82);
        const maxHp = 0; // set once the city's blocks are counted, below
        urban.push({
            id: `u${urban.length}`,
            x: site.x + inset,
            y: site.y + inset,
            w,
            h,
            z,
            tone: rng.float(.70, 1.04),
            lit: rng.chance(.45),
            model: `fabric:${model.slug}`,
            rotationY: rng.chance(.5) ? 0 : Math.PI,
            hp: maxHp,
            maxHp,
            pop: 0,
            housed: 0,
        });
    }
    // Water and roads keep the shape generated before assets existed.
    fillParks(land);
    const sites = treeSites(rng, land);
    const treeModels = rng.shuffle(TREE_MODELS.flatMap((slug) => Array.from({ length: MAX_TREE_COPIES }, () => slug)));
    const trees = [];
    for (let i = 0; i < Math.min(sites.length, treeModels.length); i++) {
        const site = sites[i];
        trees.push({
            id: `t${i}`,
            x: site.x + rng.float(.28, .72),
            y: site.y + rng.float(.28, .72),
            scale: rng.float(.8, 1.1),
            rotationY: rng.float(0, Math.PI * 2),
            model: `tree:${treeModels[i]}`,
        });
    }
    const blockHp = urbanBlockMaxHp(urban.length);
    for (const b of urban) {
        b.hp = blockHp;
        b.maxHp = blockHp;
    }
    return { urban, trees };
}
// --- City Reorganisation ----------------------------------------------------
/** Seeded layouts tried before a reorganisation gives up and leaves the city as it was. */
export const REORGANISE_ATTEMPTS = 32;
const FABRIC_BY_KEY = new Map(FABRIC_MODELS.map((m) => [`fabric:${m.slug}`, m]));
/**
 * Re-lay a whole city on its own ground: every strategic building, every
 * block of housing and trade, and the trees, moved to new sites on the same
 * river, roads and original parks (spec §8, R-01). Nothing is healed, built
 * or lost: each record keeps its health, population and model, and the
 * buildings get new opaque ids, so nothing an enemy learned by name carries
 * over. Launchers stay on their tiles; the commander re-sites them.
 *
 * The city's base ground is regenerated from its seed — the river, roads and
 * parks are the first thing a city's stream draws — and checked against what
 * the city has. Up to REORGANISE_ATTEMPTS seeded layouts are tried; if none
 * fits every building and block, nothing changes and false comes back.
 */
export function reorganiseCity(city, citySeed) {
    const base = generateTerrain(new Rng(citySeed));
    const fixed = (t) => t === 'water' || t === 'road' || t === 'bridge';
    if (base.length !== city.terrain.length || base.some((t, i) => fixed(t) !== fixed(city.terrain[i]) ||
        (fixed(t) && t !== city.terrain[i]))) {
        return false;
    }
    for (let attempt = 0; attempt < REORGANISE_ATTEMPTS; attempt++) {
        const rng = new Rng(`${citySeed}:reorg:${attempt}`);
        const terrain = [...base];
        // The strategic buildings: a fresh district plan, matched back to the
        // city's own buildings type by type.
        const placed = placeAssets(rng, terrain);
        const pool = new Map();
        for (const p of placed)
            pool.set(p.type, [...(pool.get(p.type) ?? []), p]);
        const sites = new Map();
        for (const a of city.assets) {
            const p = pool.get(a.type)?.shift();
            if (!p || p.w !== a.w || p.h !== a.h)
                break;
            sites.set(a, { x: p.x, y: p.y });
        }
        if (sites.size !== city.assets.length)
            continue;
        // The fabric around them, block for block.
        const land = fabricLand(terrain, city.assets.map((a) => ({ ...sites.get(a), w: a.w, h: a.h })));
        const blocks = new Map();
        for (const b of city.urban) {
            const model = FABRIC_BY_KEY.get(b.model);
            if (!model)
                break;
            const site = fabricSite(rng, land, model.w, model.h);
            if (!site)
                break;
            land.claim(site.x, site.y, model.w, model.h);
            const inset = (model.w - b.w) / 2;
            blocks.set(b, { x: site.x + inset, y: site.y + inset });
        }
        if (blocks.size !== city.urban.length)
            continue;
        fillParks(land);
        // It fits. Move everything, and rename what an enemy could have named.
        const old = new Set(city.assets.map((a) => a.id));
        const ids = new Rng(`${citySeed}:reorg:${attempt}:ids`);
        const used = new Set();
        for (const a of city.assets) {
            Object.assign(a, sites.get(a));
            let id;
            do
                id = `a${ids.int(0x7fffffff).toString(36)}`;
            while (used.has(id) || old.has(id));
            used.add(id);
            a.id = id;
        }
        city.urban.forEach((b, i) => {
            Object.assign(b, blocks.get(b));
            b.id = `u${ids.int(0x7fffffff).toString(36)}${i.toString(36)}`;
        });
        const open = treeSites(rng, land);
        city.trees = city.trees.slice(0, open.length).map((t, i) => ({
            ...t,
            x: open[i].x + rng.float(.28, .72),
            y: open[i].y + rng.float(.28, .72),
        }));
        city.terrain = terrain;
        return true;
    }
    return false;
}
function distributePopulation(assets, urban) {
    const housing = assets.filter((a) => a.type === 'residential');
    const weighted = [
        ...housing.map((a) => ({ target: a, weight: a.w * a.h * 1.4 })),
        ...urban.map((b) => ({ target: b, weight: b.w * b.h * b.z })),
    ];
    const totalWeight = weighted.reduce((sum, entry) => sum + entry.weight, 0);
    let assigned = 0;
    weighted.forEach((entry, index) => {
        const pop = index === weighted.length - 1
            ? START_POPULATION - assigned
            : START_POPULATION * entry.weight / totalWeight;
        entry.target.pop = pop;
        entry.target.housed = pop;
        assigned += pop;
    });
}
/**
 * Give every building an id that says nothing about it.
 *
 * Ids used to be `a0`, `a1`... in the placement queue's order, which is sorted
 * by footprint and so identical in every city: `a3` was always the command
 * bunker. An id is on the wire from the first contact, long before the type
 * is, so the id alone told an attacker what they were looking at. Drawn from a
 * stream of their own, so the layout is exactly what the seed always gave.
 */
function assignOpaqueIds(assets, seed) {
    const rng = new Rng(seed);
    const used = new Set();
    for (const a of assets) {
        let id;
        do
            id = `a${rng.int(0x7fffffff).toString(36)}`;
        while (used.has(id));
        used.add(id);
        a.id = id;
    }
}
// --- public ----------------------------------------------------------------
/**
 * A side's opening treasury: every city's old starting purse, pooled. The
 * same money a side of that size always began with, so pooling neither
 * creates nor destroys anything at the start.
 */
export function startingTreasury(cities) {
    return {
        materials: START_RESOURCES.materials * cities,
        fuel: START_RESOURCES.fuel * cities,
        credits: START_RESOURCES.credits * cities,
    };
}
export function generateCity(args) {
    const rng = new Rng(args.seed);
    const terrain = generateTerrain(rng);
    const assets = placeAssets(rng, terrain);
    const { urban, trees } = placeCityFabric(rng, terrain, assets);
    distributePopulation(assets, urban);
    assignOpaqueIds(assets, `${args.seed}:ids`);
    const development = Object.keys(DEVELOPMENT_SPECS).map((id) => ({ id, category: DEVELOPMENT_SPECS[id].category, tier: DEVELOPMENT_SPECS[id].tier, invested: 0, required: DEVELOPMENT_SPECS[id].required, done: false }));
    const housed = assets.reduce((s, a) => s + a.pop, 0) + urban.reduce((s, b) => s + b.pop, 0);
    const city = {
        id: args.id,
        name: args.name,
        owner: args.owner,
        faction: args.faction,
        mx: args.mx,
        my: args.my,
        width: CITY_W,
        height: CITY_H,
        terrain,
        assets,
        urban,
        trees,
        defences: [],
        posture: 'EMCON',
        arsenal: { ...START_ARSENAL },
        // Everyone starts with standard warheads only. Specials are found in
        // battle (discovery.ts), never built.
        warheads: { standard: 0, he: 0, cluster: 0, thermobaric: 0, chemical: 0, nuclear: 0 },
        contaminated: 0,
        frozen: 0,
        reserve: [],
        unitSeq: 0,
        ledger: {
            income: { materials: 0, fuel: 0, credits: 0 },
            spent: { materials: 0, fuel: 0, credits: 0 },
        },
        populace: {
            population: housed || START_POPULATION,
            fear: START_FEAR,
            unrest: START_UNREST,
            loyalty: START_LOYALTY,
            casualties: 0,
            evacuated: 0,
            repression: 0,
        },
        development,
        // Everybody starts balanced. Committing to a doctrine is a decision you
        // make when you can see what the war looks like, not a starting condition.
        doctrine: 'NEUTRAL',
        reorganised: false,
        // Dealt by the engine, from the slot the city lands on.
        cluster: 0,
        region: 0,
        defeated: null,
    };
    // The opening defences, as units in the reserve. Their ids come from a
    // stream of their own, like the buildings', so nothing about the layout moves.
    for (const type of Object.keys(START_DEFENCES)) {
        for (let i = 0; i < START_DEFENCES[type]; i++)
            createUnit(city, type, `${args.seed}:units`);
    }
    return city;
}
/**
 * Where the cities go.
 *
 * The layout is not decoration: it decides, before a shot is fired, who can
 * reach whom and with what. It is built in three scopes.
 *
 *  - A **cluster** is four cities a side, laid out so that every city has
 *    exactly one enemy inside strike-drone range, two inside cruise range
 *    (three with Common Cause) and all four inside ballistic range
 *    (REACH_RULE). It is the tactical fight.
 *  - A **region** is up to four clusters, set so that every enemy in another
 *    of them stands beyond extended cruise range and inside extended
 *    ballistic range: only a ballistic missile (or recon drone) with Common
 *    Cause, fired from a cluster still being fought, crosses between them
 *    (`strikeScope`).
 *  - The **theatre** is the regions, set far enough apart that nothing but an
 *    ICBM reaches from one to the next.
 *
 * The cluster and region shapes were solved offline against those reaches and
 * are fixed data, not something to nudge by eye: moving a city by 100km
 * changes how many launchers can reach it.
 */
/**
 * Solved clusters, by cities per side, and the region they are set in, from
 * `pockets.generated.ts`. A full cluster is POCKET_MAX a side; the smaller
 * ones are for the survivors of a stage who do not make a whole one.
 */
export { POCKETS, REGION };
/** The largest cluster: four against four, the unit the map is built from. */
export const POCKET_MAX = 4;
/**
 * The longest reach a city has inside its own cluster, whatever it has
 * researched: unextended ballistic range. Common Cause adds reach to cruise
 * missiles too, but only up to the cluster's third enemy, well inside this.
 * A strategic reserve stands further off than this from every enemy.
 */
export function localReachKm() {
    return Math.max(WEAPON_SPECS.strike_drone.rangeKm + EXTENDED_RANGE_ADD_KM.strike_drone, WEAPON_SPECS.cruise.rangeKm + EXTENDED_RANGE_ADD_KM.cruise, WEAPON_SPECS.ballistic.rangeKm, WEAPON_SPECS.recon_drone.rangeKm);
}
/**
 * How close another cluster of the region may stand: beyond the longest reach
 * of every weapon that never leaves its cluster (strike drones and cruise
 * missiles, Common Cause included). Four 4v4 clusters cannot all stand beyond
 * unextended ballistic range and inside extended ballistic range of each
 * other, so a neighbouring cluster may be inside a ballistic missile's
 * unextended reach. That missile still cannot go there without Common Cause:
 * the scope rule, not the distance, keeps it home (`strikeScope`).
 */
export function clusterFloorKm() {
    return Math.max(...Object.keys(WEAPON_SPECS)
        .filter((w) => w !== 'icbm' && !REGIONAL_WEAPONS.includes(w))
        .map((w) => WEAPON_SPECS[w].rangeKm + EXTENDED_RANGE_ADD_KM[w]));
}
/**
 * Extended ballistic reach: the rest of the region, and nothing beyond it.
 * Every enemy in another cluster of the region stands inside this; every enemy
 * in another region, outside it.
 */
export function regionReachKm() {
    return WEAPON_SPECS.ballistic.rangeKm + EXTENDED_RANGE_ADD_KM.ballistic;
}
/**
 * The sizes a match is played at, in cities a side: whole regions of four
 * 4v4 clusters, one to four of them (16 to 64). The tactical fight is inside a
 * cluster and a war is a set of those battles, so a board of partial clusters
 * would start some of them lopsided before a shot was fired. A battle, one
 * cluster on its own (MatchConfig.cluster), is dealt apart from these.
 */
export const MATCH_SIZES = [1, 2, 3, 4].map((regions) => regions * CLUSTERS_PER_REGION * POCKET_MAX);
/** The largest board the lobby deals. */
export const MAX_PER_SIDE = MATCH_SIZES[MATCH_SIZES.length - 1];
/** The match size nearest to a request. */
export function nearestMatchSize(n) {
    return MATCH_SIZES.reduce((best, s) => (Math.abs(s - n) < Math.abs(best - n) ? s : best), MATCH_SIZES[0]);
}
/**
 * Open ground between one region and the next, beyond what keeps them out of
 * each other's reach. It is generous on purpose: this is the number that stops
 * a big map turning into one enormous killing ground.
 */
export const REGION_CLEARANCE_KM = 400;
/**
 * How far out from its region a strategic reserve may stand. A reserve is a
 * survivor with no opponent of its own, placed clear of every enemy's local
 * weapons, and it needs somewhere to stand that is not inside a cluster.
 */
export const RESERVE_BELT_KM = 2600;
/**
 * Centre-to-centre spacing that keeps every region out of every other's
 * extended ballistic reach: the reach, plus the width of a region on each
 * side, plus its reserve belt when there are reserves to seat.
 */
export function regionPitchKm(reserves = false, radius = REGION.radius) {
    return regionReachKm() + (radius + (reserves ? RESERVE_BELT_KM : 0)) * 2 + REGION_CLEARANCE_KM;
}
/**
 * The ground a region's clusters actually stand on: the middle of the sites
 * in use and how far out they reach. A region of one or two clusters is drawn
 * round those, not round the four sites it could hold, so a small board is a
 * small map.
 */
function regionFootprint(sizes) {
    const pts = sizes.flatMap((k, site) => {
        const layout = REGION.sites[site][k];
        return [...layout.west, ...layout.east];
    });
    if (pts.length === 0)
        return { centre: { x: 0, y: 0 }, radius: 0 };
    const centre = {
        x: pts.reduce((a, p) => a + p.x, 0) / pts.length,
        y: pts.reduce((a, p) => a + p.y, 0) / pts.length,
    };
    return { centre, radius: Math.max(...pts.map((p) => Math.hypot(p.x - centre.x, p.y - centre.y))) };
}
/** Each region's footprint, and the pitch that keeps the largest of them apart. */
function layoutRegions(plan) {
    const reserves = plan.reserve.WEST + plan.reserve.EAST > 0;
    const footprints = plan.regions.map((members) => regionFootprint(members.map((c) => plan.clusters[c])));
    const widest = Math.max(0, ...footprints.map((f) => f.radius));
    const centres = hexCentres(Math.max(1, plan.regions.length), regionPitchKm(reserves, widest));
    return { footprints, centres, reserves };
}
/**
 * Hexagonal spiral of centres out from the middle of the map, then centred on
 * the origin, so a board of two or three regions is not a long thin strip with
 * the action stuck at one end.
 */
function hexCentres(count, pitch) {
    const out = [{ x: 0, y: 0 }];
    let ring = 1;
    while (out.length < count) {
        const corners = [];
        for (let k = 0; k < 6; k++) {
            const a = (k * Math.PI) / 3;
            corners.push({ x: Math.cos(a) * pitch * ring, y: Math.sin(a) * pitch * ring });
        }
        for (let k = 0; k < 6 && out.length < count; k++) {
            const from = corners[k];
            const to = corners[(k + 1) % 6];
            for (let step = 0; step < ring && out.length < count; step++) {
                out.push({
                    x: from.x + ((to.x - from.x) * step) / ring,
                    y: from.y + ((to.y - from.y) * step) / ring,
                });
            }
        }
        ring++;
    }
    const mx = out.reduce((a, c) => a + c.x, 0) / out.length;
    const my = out.reduce((a, c) => a + c.y, 0) / out.length;
    return out.map((c) => ({ x: c.x - mx, y: c.y - my }));
}
export function clusterPlan(west, east) {
    const w = Math.max(0, Math.floor(west));
    const e = Math.max(0, Math.floor(east));
    const paired = Math.min(w, e);
    const clusters = [];
    for (let left = paired; left > 0; left -= POCKET_MAX)
        clusters.push(Math.min(POCKET_MAX, left));
    const regionCount = Math.ceil(clusters.length / CLUSTERS_PER_REGION);
    const regions = [];
    let next = 0;
    for (let r = 0; r < regionCount; r++) {
        const size = Math.floor(clusters.length / regionCount) + (r < clusters.length % regionCount ? 1 : 0);
        regions.push(Array.from({ length: size }, () => next++));
    }
    return { clusters, regions, reserve: { WEST: w - paired, EAST: e - paired } };
}
/**
 * Where one strategic reserve stands.
 *
 * Out of every enemy's local reach (so no drone, cruise missile or unextended
 * ballistic missile can touch it), and out of extended ballistic reach of
 * every enemy in another region; inside that reach of as many of its own
 * region's enemies as it can manage, because a reserve is meant to stay a
 * target for the regional and strategic weapons rather than leave the war.
 * Then as close to the middle of its region as all that allows. Searched on a
 * fixed grid, so the same board always seats its reserves the same way.
 */
function placeReserve(centre, radius, region, enemies, friends) {
    const local = localReachKm() + 100;
    const reach = regionReachKm();
    let best = { x: centre.x, y: centre.y };
    let bestScore = Infinity;
    const outer = radius + RESERVE_BELT_KM;
    for (let r = 0; r <= outer; r += 100) {
        const steps = r === 0 ? 1 : Math.max(8, Math.round((2 * Math.PI * r) / 150));
        for (let i = 0; i < steps; i++) {
            const a = (i / steps) * Math.PI * 2;
            const x = centre.x + Math.cos(a) * r;
            const y = centre.y + Math.sin(a) * r;
            let v = 0;
            for (const e of enemies) {
                const d = Math.hypot(x - e.x, y - e.y);
                if (d < local)
                    v += (local - d) * 10;
                if (e.region === region) {
                    if (d > reach - 100)
                        v += d - (reach - 100);
                }
                else if (d < reach + 100)
                    v += (reach + 100 - d) * 10;
            }
            for (const f of friends) {
                const d = Math.hypot(x - f.x, y - f.y);
                if (d < MIN_SEPARATION_KM + 80)
                    v += (MIN_SEPARATION_KM + 80 - d) * 10;
            }
            const score = v * 1000 + r;
            if (score < bestScore) {
                bestScore = score;
                best = { x, y };
            }
        }
    }
    return best;
}
/**
 * Lay the whole board out: clusters in regions, regions spiralling out from
 * the centre, and any strategic reserve seated beside its region.
 *
 * Each region is rotated by a seeded angle so no two matches look identical.
 * Rotation is the only randomness allowed here, because it is the only change
 * that leaves every distance inside the region exactly as it was solved.
 *
 * Sides need not be the same size: this deals the board a stage re-forms into
 * as well as the first one, from whoever is still standing.
 */
export function generateBoard(westCount, eastCount, seed, mapW, mapH) {
    const rng = new Rng(`${seed}:complex`);
    const plan = clusterPlan(westCount, eastCount);
    const { footprints, centres } = layoutRegions(plan);
    const cx = mapW / 2;
    const cy = mapH / 2;
    const out = [];
    const next = { WEST: 0, EAST: 0 };
    const push = (faction, q, cluster, region) => {
        const mx = cx + q.x;
        const my = cy + q.y;
        out.push({ faction, index: next[faction]++, mx, my, cluster, region, radius: Math.hypot(mx - cx, my - cy) });
    };
    plan.regions.forEach((members, region) => {
        const centre = centres[region];
        const mid = footprints[region].centre;
        const theta = rng.float(0, Math.PI * 2);
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);
        // Which side takes which of the solved positions, drawn per region. The
        // layout keeps every promise either way round, but the two sets of
        // positions are not mirror images of each other, so a fixed assignment
        // handed one colour the same ground on every board.
        const swap = rng.float(0, 1) < 0.5;
        const west = swap ? 'EAST' : 'WEST';
        const east = swap ? 'WEST' : 'EAST';
        // Turned about the middle of the sites in use, and set on the region's centre.
        const turn = (q) => {
            const x = q.x - mid.x;
            const y = q.y - mid.y;
            return { x: centre.x + x * cos - y * sin, y: centre.y + x * sin + y * cos };
        };
        members.forEach((cluster, site) => {
            const k = plan.clusters[cluster];
            const layout = REGION.sites[site][k];
            for (const q of layout.west)
                push(west, turn(q), cluster, region);
            for (const q of layout.east)
                push(east, turn(q), cluster, region);
        });
    });
    // The larger side's surplus, a region at a time in turn.
    for (const faction of ['WEST', 'EAST']) {
        const count = plan.reserve[faction];
        if (count === 0)
            continue;
        const regions = Math.max(1, plan.regions.length);
        for (let i = 0; i < count; i++) {
            const region = i % regions;
            const centre = centres[region];
            const enemies = out
                .filter((s) => s.faction !== faction)
                .map((s) => ({ x: s.mx - cx, y: s.my - cy, region: s.region }));
            const friends = out.filter((s) => s.faction === faction).map((s) => ({ x: s.mx - cx, y: s.my - cy }));
            push(faction, placeReserve(centre, footprints[region].radius, region, enemies, friends), -1, region);
        }
    }
    return out;
}
/** The even-sided case, which is how every match starts. */
export function generateComplex(perSide, seed, mapW, mapH) {
    return generateBoard(perSide, perSide, seed, mapW, mapH);
}
/**
 * The band each of a city's enemies must fall in, nearest first.
 *
 * Read straight off REACH_RULE: the k-th nearest enemy has to be beyond every
 * reach that promises fewer than k enemies, and within every reach that
 * promises k or more. With the rule at 1 / 2 / 4 (cruise 3 extended) that is
 * one enemy inside a strike drone's range, a second inside cruise range, the third between
 * cruise range and extended cruise range, and the fourth between extended
 * cruise range and ballistic range.
 */
export function reachBands() {
    const limits = [];
    for (const [w, rule] of Object.entries(REACH_RULE)) {
        const r = WEAPON_SPECS[w].rangeKm;
        limits.push({ km: r, count: rule.base });
        limits.push({ km: r + EXTENDED_RANGE_ADD_KM[w], count: rule.extended });
    }
    const out = [];
    for (let k = 1; k <= POCKET_MAX; k++) {
        let lo = MIN_SEPARATION_KM;
        let hi = Infinity;
        for (const l of limits) {
            if (l.count < k)
                lo = Math.max(lo, l.km);
            else
                hi = Math.min(hi, l.km);
        }
        out.push([lo, hi]);
    }
    return out;
}
/** How far a set of distances sits outside the bands, in km. */
export function bandError(distances) {
    const b = reachBands();
    const outer = b[b.length - 1][1];
    const ds = [...distances].sort((x, y) => x - y);
    let err = 0;
    for (let k = 0; k < ds.length; k++) {
        if (k < b.length) {
            if (ds[k] < b[k][0])
                err += b[k][0] - ds[k];
            else if (ds[k] > b[k][1])
                err += ds[k] - b[k][1];
        }
        else if (ds[k] <= outer) {
            err += outer - ds[k] + 1;
        }
    }
    return err;
}
/** Nothing may be closer to a friendly than this, or the map is unreadable. */
export const MIN_SEPARATION_KM = 420;
// --- map geometry ----------------------------------------------------------
/**
 * Open ground between the outermost city and the edge of the chart.
 *
 * Its job is dogleg room: a route that swings wide to come in off an unexpected
 * bearing has to have somewhere to swing to, and a waypoint cannot be dragged
 * off the sheet. So it is set from reach rather than from board size — the
 * widest excursion worth flying is bounded by the weapon, not by how many
 * cities are playing.
 *
 * It was 900, which is about twice what any dogleg uses. On a big board that
 * is invisible; at 1v1 it made two cities 490km apart sit in the middle of a
 * 2300km sheet, occupying a seventh of it, and the plotting table turned that
 * from a wasteful layout into an obviously wasteful one: a chart is a physical
 * sheet and you can see how much of it is empty sea.
 */
const MARGIN_KM = 500;
/**
 * Map extent for a match size: whatever the regions need, plus a margin.
 *
 * This grows with the number of players rather than squeezing them together,
 * which is the whole point. Packing more cities into a fixed map is what turns
 * a large game into a bloodbath where the middle is in range of everything.
 */
export function mapSizeFor(perSide) {
    return boardSizeFor(perSide, perSide);
}
/**
 * Map extent for a board dealt to two sides of these sizes.
 *
 * It shrinks as the war does. A board still drawn for twenty cities when six
 * are left is mostly empty ground, and the survivors would be scattered across
 * it far outside each other's reach.
 */
export function boardSizeFor(west, east) {
    const plan = clusterPlan(west, east);
    if (plan.regions.length === 0)
        return { mapW: 2000, mapH: 2000 };
    const { footprints, centres, reserves } = layoutRegions(plan);
    // The chart draws each region as a landmass whose coast swings out by up to
    // a quarter of its width, so the margin grows with the region.
    const reach = (r) => (r + (reserves ? RESERVE_BELT_KM : 0)) * 1.3 + MARGIN_KM;
    const halfW = Math.max(...centres.map((c, i) => Math.abs(c.x) + reach(footprints[i].radius)));
    const halfH = Math.max(...centres.map((c, i) => Math.abs(c.y) + reach(footprints[i].radius)));
    return { mapW: Math.round(halfW * 2), mapH: Math.round(halfH * 2) };
}
export function distanceKm(a, b) {
    return Math.hypot(a.mx - b.mx, a.my - b.my);
}
//# sourceMappingURL=citygen.js.map